import { adapterClient, handleApiError } from './client';
import { compensationPolicySchema } from '@/types/current-project';
import type {
  CatalogView,
  ClientProfile,
  ProfilesView,
  WorkerProfile,
  WorkerSummary,
  WorkerRegistration,
  OperationRef,
  OperationView,
} from '@/types/session';
import {
  validateImage,
  validateContactEmail,
  validatePersonalInfo,
  validateProfileText,
  validateProfileUrl,
} from '@/lib/auth-validation';

export async function getProfiles(): Promise<ProfilesView> {
  try {
    return (await adapterClient.get<ProfilesView>('/profiles/me')).data;
  } catch (error) {
    handleApiError(error, 'profiles');
  }
}
export async function putProfile(
  section: 'client',
  profile: ClientProfile
): Promise<ProfilesView>;
export async function putProfile(
  section: 'worker',
  profile: WorkerProfile
): Promise<ProfilesView>;
export async function putProfile(
  section: 'client' | 'worker',
  profile: ClientProfile | WorkerProfile
): Promise<ProfilesView> {
  validateProfileText(profile.name, 'Name', 128, true);
  validateProfileText(profile.location ?? '', 'Location', 128);
  if (profile.contactEmail != null) validateContactEmail(profile.contactEmail);
  if (profile.languages.length > 32)
    throw new Error('Choose at most 32 languages.');
  profile.languages.forEach((language) =>
    validateProfileText(language, 'Language', 64, true)
  );
  if ('company' in profile) {
    validateProfileText(profile.company ?? '', 'Company', 128);
    validateProfileText(profile.department ?? '', 'Department', 128);
    validateProfileText(profile.description ?? '', 'Description', 4096);
    validateProfileUrl(profile.website ?? '', 'Website');
  }
  if ('biography' in profile) {
    const experiences = profile.roleExperiences ?? [];
    if (
      experiences.length > 64 ||
      new Set(experiences.map((entry) => entry.roleId)).size !==
        experiences.length ||
      experiences.some(
        (entry) =>
          !Number.isSafeInteger(entry.roleId) ||
          entry.roleId < 1 ||
          entry.roleId > 0xffffffff ||
          !['junior', 'mid-level', 'senior'].includes(entry.proficiency)
      )
    )
      throw new Error(
        'Review the experience values and choose each role only once.'
      );
    validatePersonalInfo(
      profile.name,
      profile.githubUsername ?? '',
      profile.portfolioUrl ?? ''
    );
    validateProfileText(profile.biography ?? '', 'Biography', 4096);
    validateProfileText(profile.background ?? '', 'Background', 4096);
  }
  try {
    return (
      await adapterClient.put<ProfilesView>('/profiles/me', {
        section,
        profile,
      })
    ).data;
  } catch (error) {
    handleApiError(error, 'save-profile');
  }
}
export async function putProfileImage(
  section: 'client' | 'worker',
  image: File
) {
  validateImage(image);
  try {
    await adapterClient.put(`/profiles/me/${section}/image`, image, {
      headers: { 'Content-Type': image.type },
    });
  } catch (error) {
    handleApiError(error, 'save-image');
  }
}
export async function getCatalog(): Promise<CatalogView> {
  try {
    const { data } = await adapterClient.get<CatalogView>('/catalog');
    return {
      ...data,
      compensationPolicy: compensationPolicySchema.parse(
        data.compensationPolicy
      ),
    };
  } catch (error) {
    handleApiError(error, 'catalog');
  }
}
export async function getWorkers(): Promise<WorkerSummary[]> {
  try {
    return (await adapterClient.get<WorkerSummary[]>('/workers')).data;
  } catch (error) {
    handleApiError(error, 'workers');
  }
}
export async function registerWorker(
  payload: WorkerRegistration,
  key: string
): Promise<OperationRef> {
  try {
    return (
      await adapterClient.post<OperationRef>('/workers', payload, {
        headers: { 'Idempotency-Key': key },
      })
    ).data;
  } catch (error) {
    handleApiError(error, 'register-worker');
  }
}
export async function getOperation(id: string): Promise<OperationView> {
  try {
    return (
      await adapterClient.get<OperationView>(
        `/operations/${encodeURIComponent(id)}`
      )
    ).data;
  } catch (error) {
    handleApiError(error, 'operation');
  }
}

export async function updateWorker(
  section: 'qualifications' | 'calendar',
  payload: unknown,
  key: string
): Promise<OperationRef> {
  try {
    return (
      await adapterClient.put<OperationRef>(`/workers/me/${section}`, payload, {
        headers: { 'Idempotency-Key': key },
      })
    ).data;
  } catch (error) {
    handleApiError(error, `update-${section}`);
  }
}
